import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useAsync, ErrorAlert } from './ui.jsx';
import { companyHistory, mergerBoxes, ownersNear, reportFor, reportPending, investment } from '../data/history.js';
import { hasFinancials } from '../data/financials.js';
import { financialsOf, financialXml, mergerPartners } from '../data/db.js';
import { danishDate, money } from '../data/format.js';
import { companyPath } from '../data/relations.js';

// The registration history of a company as a timeline: a vertical line, one dot for
// each date, and the changes of that date next to the dot. A capital increase gets a
// box with the money: the amount comes from the annual report (history.js).

const FIRST_GROUPS = 12;
// Each amount costs one annual report (about 20 KB), so only the newest increases get one.
const MAX_AMOUNTS = 5;
const PATHS = { person: '/Person/', other: '/Person/' };
const LEGEND = [
  { kind: 'owner', label: 'Ejere' },
  { kind: 'role', label: 'Ledelse' },
  { kind: 'capital', label: 'Kapital' },
  { kind: 'merger', label: 'Fusion' },
  { kind: 'name', label: 'Navn og form' },
  { kind: 'other', label: 'Andet' },
];

function isIncrease(event) {
  return event.kind === 'capital' && event.from !== null && event.to > event.from;
}

function percent(fraction) {
  return `${(fraction * 100).toFixed(1).replace('.', ',')} %`;
}

function Who({ who }) {
  if (!who.enhedsNummer) return who.name;
  const to = PATHS[who.kind] ? `${PATHS[who.kind]}${who.enhedsNummer}` : companyPath(who);
  return <Link to={to}>{who.name}</Link>;
}

function EventText({ event }) {
  if (!event.who) return event.text;
  return <><Who who={event.who} /> {event.action}</>;
}

// The amounts of the newest capital increases: { [date]: investment(...) + pending }.
async function loadAmounts(company, increases) {
  const amounts = {};
  if (increases.length === 0 || !hasFinancials(company.virksomhedsformkort)) return amounts;
  const years = (await financialsOf(company.enhedsNummer))
    .map((row) => ({
      start: row.start ?? (row.startDate ? String(row.startDate).slice(0, 10) : null),
      end: row.report?.period?.endDate ?? (row.endDate ? String(row.endDate).slice(0, 10) : null),
    }))
    .filter((year) => year.start && year.end);
  const perYear = new Map();
  for (const increase of increases) {
    const year = reportFor(increase.date, years);
    if (year) perYear.set(year.start, (perYear.get(year.start) ?? 0) + 1);
  }
  for (const increase of increases.slice(0, MAX_AMOUNTS)) {
    const year = reportFor(increase.date, years);
    const row = year ? await financialXml(company.enhedsNummer, year.start) : null;
    const report = row?.xml ? { start: year.start, end: year.end, xml: row.xml } : null;
    amounts[increase.date] = {
      ...investment(increase, report, year ? perYear.get(year.start) : 1),
      pending: !year && reportPending(increase.date, years),
    };
  }
  return amounts;
}

function noteOf(facts, nominal) {
  if (facts.paid !== null) {
    const label = facts.reportLabel;
    let note;
    if (facts.source === 'equity') {
      note = `Fra egenkapitalopgørelsen i årsrapporten ${label}: kapitalforhøjelse ${money(facts.capital)} og overkurs ${money(facts.premium)}.`;
    } else if (facts.source === 'transfer') {
      note = `Beregnet ud fra årsrapporten ${label}: kapitalen steg med ${money(facts.capital)}, og en ny overkurs på ${money(facts.premium)} blev overført til overført resultat i samme år.`;
    } else {
      note = `Beregnet ud fra årsrapporten ${label}: kapitalen steg med ${money(facts.capital)}, og overkursen steg med ${money(facts.premium)}.`;
    }
    note += ' CVR oplyser ikke beløbet direkte, så det er et skøn.';
    if (facts.shared > 1) note += ` Regnskabsåret har ${facts.shared} kapitalforhøjelser, og beløbet gælder dem alle.`;
    return note;
  }
  if (facts.pending) return 'Årsrapporten for dette regnskabsår er ikke offentliggjort endnu, eller agenten har ikke hentet den. Beløbet er derfor ikke kendt.';
  if (facts.reportLabel) {
    return `Årsrapporten ${facts.reportLabel} viser ingen ny overkurs. Forhøjelsen kan være betalt til kurs 100 (${money(facts.capital ?? nominal)}), eller være sket på en anden måde, for eksempel ved konvertering af gæld.`;
  }
  return 'Årsrapporten for dette regnskabsår er ikke hentet, så beløbet er ikke kendt.';
}

function Investment({ increase, facts, loading, owners }) {
  const nominal = increase.to - increase.from;
  const newShare = nominal / increase.to;
  let note;
  if (loading) note = 'Henter årsrapporten…';
  else if (!facts) note = `Beløbet hentes kun for de ${MAX_AMOUNTS} nyeste kapitalforhøjelser.`;
  else note = noteOf(facts, nominal);

  return (
    <div className="invest">
      <div className="invest-top">
        <div>
          <div className="invest-label">Kapitalforhøjelse</div>
          {facts?.paid != null ? (
            <>
              <div className="invest-amount">{money(facts.paid)}</div>
              <div className="invest-sub">
                {facts.shared > 1 ? `indskudt i regnskabsåret ${facts.reportLabel} (skøn)` : 'indskudt i selskabet (skøn)'}
              </div>
            </>
          ) : (
            <div className="invest-amount unknown">{loading ? '…' : 'Beløb ikke oplyst'}</div>
          )}
        </div>
        {facts?.valuation != null && (
          <div>
            <div className="invest-label">Værdi af selskabet efter</div>
            <div className="invest-value">≈ {money(facts.valuation)}</div>
          </div>
        )}
      </div>

      <div className="share-bar" role="img"
        aria-label={`Eksisterende kapital ${percent(1 - newShare)}, ny kapital ${percent(newShare)}`}>
        <span className="old" style={{ width: `${(1 - newShare) * 100}%` }} />
        <span className="new" style={{ width: `${newShare * 100}%` }} />
      </div>
      <div className="share-legend">
        <span><i className="old" />Eksisterende kapital {money(increase.from)} · {percent(1 - newShare)}</span>
        <span><i className="new" />Ny kapital {money(nominal)} · {percent(newShare)}</span>
      </div>

      {(owners.length > 0 || facts?.premium != null) && (
        <dl className="invest-facts">
          {owners.length > 0 && (
            <div>
              <dt>Nye ejere omkring datoen</dt>
              <dd>
                {owners.map((owner, i) => (
                  <span key={`${owner.who.enhedsNummer}-${i}`} className="invest-owner">
                    <Who who={owner.who} /> {owner.action.replace(/^bliver ejer\s*/, '')}
                  </span>
                ))}
              </dd>
            </div>
          )}
          {facts?.premium != null && <div><dt>Overkurs</dt><dd>{money(facts.premium)}</dd></div>}
          {facts?.pricePerKrone != null && <div><dt>Pris pr. 1 kr. kapital</dt><dd>{money(facts.pricePerKrone)}</dd></div>}
        </dl>
      )}
      <p className="invest-note">{note}</p>
    </div>
  );
}

// One side of a merger in the box: the companies, and if they stop or continue.
function MergerSide({ companies, stops, loading }) {
  return (
    <div className={`merger-side ${stops ? 'stops' : 'continues'}`}>
      {companies.map((c, i) => (
        <div key={i} className="merger-name">{c.who ? <Who who={c.who} /> : c.unknown && loading ? '…' : c.name}</div>
      ))}
      <div className="merger-state">{stops ? 'ophørt' : 'fortsætter'}</div>
    </div>
  );
}

// A merger as a box: the companies that stop, an arrow, and the company that continues.
function Merger({ box, loading }) {
  let note = 'Fra CVR. Selskaberne i fusionen har det samme fusionsnummer.';
  if (!box.found && loading) note = 'Søger efter de andre selskaber i fusionen…';
  else if (!box.found) note = 'CVR registrerer kun et fusionsnummer for fusionen. Siden fandt ikke andre selskaber med det samme fusionsnummer.';

  return (
    <div className="merger">
      <div className="invest-label">Fusion</div>
      <div className="merger-flow">
        <MergerSide companies={box.stopping} stops loading={loading} />
        <span className="merger-arrow" aria-label="indgår i">→</span>
        <MergerSide companies={box.continuing} stops={false} loading={loading} />
      </div>
      <dl className="invest-facts">
        <div><dt>Dette selskab</dt><dd>{box.continues ? 'Fortsættende selskab' : 'Ophørende selskab'}</dd></div>
        {box.partnerCvr != null && <div><dt>CVR-nr. på det andet selskab</dt><dd>{box.partnerCvr}</dd></div>}
        <div><dt>Fusionsnummer i CVR</dt><dd>{box.unit}</dd></div>
      </dl>
      <p className="invest-note">{note}</p>
    </div>
  );
}

export default function CompanyHistory({ company }) {
  const [showAll, setShowAll] = useState(false);
  // The other side of a merger needs lookups, so the timeline first shows the merger without it.
  const partners = useAsync(() => mergerPartners(company), [company.enhedsNummer]);
  const groups = useMemo(() => companyHistory(company, partners.data ?? []), [company, partners.data]);
  const increases = useMemo(
    () => groups.flatMap((g) => g.events.filter(isIncrease).map((e) => ({ date: g.date, from: e.from, to: e.to }))),
    [groups],
  );
  const amounts = useAsync(() => loadAmounts(company, increases), [company.enhedsNummer, increases.length]);
  const shown = showAll ? groups : groups.slice(0, FIRST_GROUPS);

  return (
    <section className="card">
      <div className="card-head">
        <h2>Historik</h2>
        {groups.length > 0 && <span className="count">{groups.length} datoer</span>}
      </div>
      <div className="card-body">
        {groups.length === 0 ? (
          <p className="muted">CVR har ingen registreringer med datoer for dette selskab.</p>
        ) : (
          <>
            <ul className="legend tl-legend">
              {LEGEND.map((item) => <li key={item.kind}><span className={`tl-key tl-${item.kind}`} />{item.label}</li>)}
            </ul>
            <ErrorAlert error={partners.error} />
            <ErrorAlert error={amounts.error} />
            <ol className="timeline">
              {shown.map((group) => {
                const increase = group.events.find(isIncrease);
                const mergers = mergerBoxes(company, group.events);
                return (
                  <li key={group.date} className={`tl-item tl-${increase ? 'invest' : mergers.length > 0 ? 'merge' : group.events[0].kind}`}>
                    <span className="tl-dot" aria-hidden="true" />
                    <time className="tl-date" dateTime={group.date}>{danishDate(group.date)}</time>
                    <ul className="tl-events">
                      {group.events.map((event, i) => (
                        <li key={i} className={`tl-event tl-${event.kind}`}><EventText event={event} /></li>
                      ))}
                    </ul>
                    {mergers.map((box) => <Merger key={box.unit} box={box} loading={partners.data === undefined && !partners.error} />)}
                    {increase && (
                      <Investment
                        increase={increase}
                        facts={amounts.data?.[group.date]}
                        loading={amounts.data === undefined && !amounts.error}
                        owners={ownersNear(groups, group.date)}
                      />
                    )}
                  </li>
                );
              })}
            </ol>
            {groups.length > FIRST_GROUPS && (
              <button type="button" className="btn timeline-more" onClick={() => setShowAll((v) => !v)}>
                {showAll ? 'Vis færre' : `Vis hele historikken (${groups.length} datoer)`}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}
